/**
 * Portal standalone assistant surface (ADR-0049 Slice D) — the AUTONOMOUS face
 * of an app's `chat` / `voice` interface, rendered FULL-PAGE rather than as the
 * floating widget that `PortalShell` docks into a page.
 *
 *   /portal/:appSlug/chat   → ChatGPT-style full-page chat
 *   /portal/:appSlug/voice  → Jarvis-style full-screen voice HUD
 *
 * It reuses the SAME runtime brains (`ChatAssistant` / `VoiceAssistant`) in their
 * `standalone` mode — no fork of the SSE / RLS / scope logic. This page only owns
 * the chrome: the portal auth gate (sign in first), the app theme, and a back
 * control (the assistant's ✕ → the app home). It does NOT mount `PortalShell`
 * (that would re-add the floating launcher on top of the full-page surface).
 */
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import { ChatAssistant } from "@/components/portal/ChatAssistant";
import { InterfaceSwitcher } from "@/components/portal/InterfaceSwitcher";
import { VoiceAssistant } from "@/components/portal/VoiceAssistant";
import { portalApi } from "@/lib/api-portal";
import { themeStyle } from "@/lib/portalLayouts";
import {
  parentLoginPath,
  portalHomePath,
  usePortalAuth,
} from "@/lib/portalAuth";

export function PortalAssistantPage({ surface }: { surface: "chat" | "voice" }) {
  const { appSlug = "" } = useParams();
  const navigate = useNavigate();
  const { token, user } = usePortalAuth();

  const brandingQ = useQuery({
    queryKey: ["portal-branding", appSlug],
    queryFn: () => portalApi.getBranding(appSlug),
    staleTime: 5 * 60_000,
    // Gated on auth too: an anonymous visitor redirects to login below, so don't
    // fire the (public but pointless) branding fetch for them.
    enabled: !!appSlug && !!token,
  });

  // Standalone assistants are signed-in surfaces (the brain runs under the
  // caller's RLS + AssistantScope) — an anonymous visitor authenticates first.
  if (!token || !user) {
    const here = `${portalHomePath(appSlug)}/${surface}`;
    return <Navigate to={parentLoginPath(here)} replace />;
  }

  if (brandingQ.isPending) {
    return (
      <div className="tray-light flex min-h-screen items-center justify-center bg-background text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  const branding = brandingQ.data;
  // The interface must expose a STANDALONE surface for this app — otherwise there's
  // no front-door to render here (an embed-only assistant has a widget, not a page).
  // Gate on `*_standalone`, NOT `*_enabled` (the embedded-widget flag). Bounce to the
  // app home (which resolves a real surface) when there's nothing standalone here.
  const enabled = surface === "voice" ? branding?.voice_standalone : branding?.chat_standalone;
  if (!enabled) return <Navigate to={portalHomePath(appSlug)} replace />;

  const theme = branding?.theme ?? {};
  // The assistant's ✕ leaves the standalone surface for the app home.
  const goHome = () => navigate(portalHomePath(appSlug));

  // Voice: the HUD is its own full-screen dark overlay — the wrapper just hosts
  // it + gates auth. Chat: the wrapper paints the themed page the chat sits on.
  return (
    <div
      className="tray-light relative flex h-screen flex-col overflow-hidden"
      style={{
        ...themeStyle(branding?.layout, theme),
        background: "var(--portal-bg, hsl(var(--bg)))",
        color: "var(--portal-text, hsl(var(--fg)))",
      }}
    >
      {surface === "chat" ? (
        <ChatAssistant
          appSlug={appSlug}
          appName={branding?.app_name}
          standalone
          onClose={goHome}
          switcher={<InterfaceSwitcher appSlug={appSlug} current="chat" />}
        />
      ) : (
        <VoiceAssistant
          appSlug={appSlug}
          appName={branding?.app_name}
          sttMode={branding?.voice_stt === "server" ? "server" : "browser"}
          ttsMode={branding?.voice_tts === "server" ? "server" : "browser"}
          wakeWord={!!branding?.voice_wake_word}
          standalone
          onClose={goHome}
          switcher={<InterfaceSwitcher appSlug={appSlug} current="voice" />}
        />
      )}
    </div>
  );
}
